import "./DeleteSectionModal.css";

import { overlay } from "overlay-kit";
import { type FC, useState } from "react";

import { SectionKind } from "components/server-types";
import { t } from "i18n/i18n";
import { ConfirmationDialog } from "mds/components/ConfirmationDialog";
import { LegacyTextField } from "mds/components/LegacyTextField";
import { useAppSelector } from "store";
import { selectCourseCodeBySectionId } from "store/selectors/section";

export const showDeleteConfirmationModal = ({
  sectionId,
  sectionTitle,
  sectionKind,
}: {
  sectionId: string;
  sectionTitle: string;
  sectionKind: SectionKind;
}) =>
  overlay.openAsync<boolean>(({ isOpen, close }) => (
    <DeleteSectionModal
      open={isOpen}
      sectionId={sectionId}
      sectionKind={sectionKind}
      sectionTitle={sectionTitle}
      onClose={close}
      onConfirm={() => {}}
    />
  ));

type DeleteSectionModalProps = {
  sectionId: string;
  sectionTitle: string;
  sectionKind: SectionKind;
  open: boolean;
  onClose: (confirmed: boolean) => void;
  onConfirm: () => void;
};

// eslint-disable-next-line react-refresh/only-export-components
const DeleteSectionModal: FC<DeleteSectionModalProps> = ({
  sectionId,
  sectionTitle,
  sectionKind,
  open,
  onClose,
  onConfirm,
}) => {
  const isTemplate = sectionKind === SectionKind.TEMPLATE;
  const deleteSectionTitle = isTemplate
    ? t("section_list_panel.delete_template_section_title")
    : t("confirm_delete_modal.delete_item", {
        item: t("glossary.section"),
      });
  const confirmButtonText = isTemplate
    ? t("section_list_panel.delete_template_section_confirm")
    : deleteSectionTitle;

  const [disableDelete, setDisableDelete] = useState<boolean>(true);

  const courseCode = useAppSelector((state) => selectCourseCodeBySectionId(state, sectionId));
  const handleCourseConfirmationCheck = (text: string) => {
    setDisableDelete(text !== courseCode);
  };

  return (
    <ConfirmationDialog
      confirmButtonDisabled={disableDelete}
      confirmButtonKind="caution"
      confirmButtonText={confirmButtonText}
      description={t("confirm_delete_modal.delete_label", { name: sectionTitle })}
      open={open}
      title={deleteSectionTitle}
      onClose={onClose}
      onConfirm={onConfirm}
    >
      <div className="section-delete-modal mt-8 flex flex-col items-start gap-8">
        <div className="section-delete-warning">
          {isTemplate
            ? t("section_list_panel.delete_template_section_warning")
            : t("section_list_panel.delete_section_warning")}
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="course-code">{t("section_list_panel.course_confirm_label")}</label>

          <LegacyTextField
            id="course-code"
            placeholder={t("section_list_panel.course_placeholder")}
            isEditing
            onChange={(event) => handleCourseConfirmationCheck(event.target.value)}
          />
        </div>
      </div>
    </ConfirmationDialog>
  );
};
